import { Injectable } from '@angular/core';

export interface HeadwayConfig {
  selector: string;
  account: string;
  trigger?: string;
  enabled?: boolean;
  widgetPosition?: string;
  callbacks?: {
    onWidgetReady?: (widget: { getUnseenCount: () => number }) => void;
    onShowWidget?: () => void;
    onHideWidget?: () => void;
  };
}

declare global {
  interface Window {
    Headway?: {
      init: (config?: HeadwayConfig) => void;
      hide: () => void;
    };
  }
}

/** Public Headway account ID for the Kahi changelog widget. */
export const HEADWAY_ACCOUNT_ID = 'xDeqj7';

const HEADWAY_SCRIPT_URL = 'https://cdn.headwayapp.co/widget.js';

/**
 * Loads the Headway changelog widget and initializes it against a DOM selector.
 * Prefer Headway.init() for SPAs so the badge rebinds after Angular re-renders.
 * @see https://docs.headwayapp.co/widget
 */
@Injectable({
  providedIn: 'root',
})
export class HeadwayService {
  private scriptLoadPromise: Promise<void> | null = null;
  private closeButton: HTMLButtonElement | null = null;

  loadScript(): Promise<void> {
    if (window.Headway) {
      return Promise.resolve();
    }
    if (this.scriptLoadPromise) {
      return this.scriptLoadPromise;
    }

    this.scriptLoadPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(
        `script[src="${HEADWAY_SCRIPT_URL}"]`
      );
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener(
          'error',
          () => reject(new Error('Failed to load Headway widget script')),
          { once: true }
        );
        return;
      }

      const script = document.createElement('script');
      script.src = HEADWAY_SCRIPT_URL;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () =>
        reject(new Error('Failed to load Headway widget script'));
      document.head.appendChild(script);
    });

    return this.scriptLoadPromise;
  }

  async init(config: Omit<HeadwayConfig, 'account'> & { account?: string }): Promise<void> {
    try {
      await this.loadScript();
      if (!window.Headway) {
        return;
      }
      window.Headway.init({
        account: HEADWAY_ACCOUNT_ID,
        ...config,
        callbacks: {
          ...config.callbacks,
          onShowWidget: () => {
            this.showCloseButton();
            config.callbacks?.onShowWidget?.();
          },
          onHideWidget: () => {
            this.removeCloseButton();
            config.callbacks?.onHideWidget?.();
          },
        },
      });
    } catch (error) {
      console.error('Failed to initialize Headway widget', error);
    }
  }

  /**
   * Headway's popup is a cross-origin iframe, so we attach a close control to the
   * parent-page frame container instead of injecting into the iframe document.
   */
  private showCloseButton(): void {
    if (this.closeButton) {
      return;
    }

    const frame = document.querySelector<HTMLElement>(
      '#HW_frame_cont, .HW_frame_cont'
    );
    if (!frame) {
      return;
    }

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'headway-close-button';
    button.setAttribute('aria-label', 'Close changelog');
    button.innerHTML = '&times;';
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      window.Headway?.hide();
    });

    frame.appendChild(button);
    this.closeButton = button;
  }

  private removeCloseButton(): void {
    this.closeButton?.remove();
    this.closeButton = null;
  }
}
